import { createFileRoute } from "@tanstack/react-router"
import { trpc } from "lib/trpc"
import { AlertCircle, FileText, Plus } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { toast } from "sonner"
import { ClientHeader } from "@/components/client-header"
import { ClientTabNavigation } from "@/components/client-tab-navigation"
import { DocumentList } from "@/components/documents/document-list"
import { EstateRelationshipTree } from "@/components/estate-relationship-tree"
import { UploadDocumentModal } from "@/components/modals/upload-document-modal"
import { Page } from "@/components/page"
import { RecommendationsSection } from "@/components/recommendations-section"
import { RefineReportsSection } from "@/components/refine-reports-section"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Typography } from "@/components/ui/typography"
import type { documentTypeEnum } from "../../../../../../../../api/db/schema/document"
import { ReportTypes } from "../../../../../../../../api/report-generation/report-types"

type DocumentType = (typeof documentTypeEnum.enumValues)[number]

export const Route = createFileRoute("/_authed/_professional/professional/reports/will/$estateId")({
  component: RouteComponent,
})

function RouteComponent() {
  const { estateId } = Route.useParams()
  const { t } = useTranslation()
  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  const [additionalPrompt, setAdditionalPrompt] = useState("")

  // Last Will & Testament enum value
  const willDocumentType: DocumentType = "LAST_WILL_AND_TESTAMENT"

  // Fetch WILL_REVIEW reports for this estate
  const { data: willReports = [] } = trpc.reports.getOfType.useQuery({
    estateId,
    reportTypes: ["WILL_REVIEW"],
  })

  // Generate will report mutation
  const queryUtils = trpc.useUtils()
  const generateWillReportMutation = trpc.reports.generate.useMutation({
    onSuccess: () => {
      toast.success(t("pages.reports.will.generationStarted"), {
        description: t("pages.reports.estate.generateReports.emailNoticeReport"),
        duration: 8000,
      })
      // Invalidate queries to refresh data when report completes
      queryUtils.reports.getOfType.invalidate({ estateId, reportTypes: ["WILL_REVIEW"] })
      queryUtils.reports.getWillInfographic.invalidate({ estateId })
    },
    onError: (error) => {
      toast.error(t("pages.reports.will.generationError", { message: error.message }))
    },
  })

  return (
    <Page
      headerSlot={<ClientHeader estateId={estateId} />}
      tabSlot={<ClientTabNavigation estateId={estateId} />}
    >
      <div className="space-y-6">
        <RecommendationsSection
          title="Last Will & Testament Recommendations"
          refineSection={
            <RefineReportsSection>
              <div className="space-y-4">
                {/* Document List */}
                <DocumentList
                  estateId={estateId}
                  documentType={willDocumentType}
                  onDocumentDeleted={() => {
                    queryUtils.documents.listByType.invalidate()
                  }}
                />

                {/* Add Document Button */}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-auto"
                  onClick={() => setUploadModalOpen(true)}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add document
                </Button>

                {/* Additional Prompt */}
                <div className="space-y-2">
                  <Label htmlFor="additionalPrompt">
                    Prompt agent when generating or rerunning the report (optional)
                  </Label>
                  <Textarea
                    id="additionalPrompt"
                    placeholder="Add specific instructions for the AI analysis..."
                    value={additionalPrompt}
                    onChange={(e) => setAdditionalPrompt(e.target.value)}
                    className="min-h-[100px] bg-white"
                  />
                </div>

                {/* Run Report Button */}
                <Button
                  type="button"
                  onClick={() => {
                    const prompt = additionalPrompt.trim() || undefined
                    toast.info(
                      prompt
                        ? `Sending report with prompt: "${prompt.slice(0, 50)}${prompt.length > 50 ? "..." : ""}"`
                        : "Sending report without additional prompt",
                    )
                    generateWillReportMutation.mutate({
                      estateId,
                      reportType: [ReportTypes.WILL_REVIEW],
                      additionalPrompt: prompt,
                    })
                  }}
                  className="w-full"
                  disabled={generateWillReportMutation.isPending}
                >
                  {generateWillReportMutation.isPending ? "Generating..." : "Run Report"}
                </Button>
                <Typography as="p" className="text-xs text-muted-foreground mt-3">
                  * Legacy Keeper reports use a complex agent orchestration to deliver personalised
                  insight for each client, saving hours of work. Due to the complex nature of the
                  report generation process you can expect each report to take between 10-20 minutes
                  as our agents evaluate the latest estate laws, user goals, and user data to
                  deliver a truly bespoke output for your clients and their families.
                </Typography>
              </div>
            </RefineReportsSection>
          }
          middleSection={<EstateRelationshipTree estateId={estateId} />}
        >
          {/* Report Insights Section - Conditional rendering based on report existence */}
          {willReports.length === 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <AlertCircle className="h-5 w-5 mr-2 text-yellow-500" />
                  No Will Reports Generated
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Typography as="p" className="text-muted-foreground">
                  Generate a will report to view insights and recommendations for this estate's last
                  will and testament.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            (() => {
              const report = willReports[0]
              if (!report) return null

              return (
                <div className="mb-6">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg overflow-hidden">
                    <div className="p-6">
                      <CardTitle className="text-[#1e40af] text-xl font-bold mb-3 flex items-center">
                        <FileText className="h-5 w-5 mr-2" />
                        Last Will and Testament Insights
                      </CardTitle>

                      {report.summaryNotes ? (
                        <Typography as="p" className="text-gray-700 mb-4 font-medium">
                          {report.summaryNotes}
                        </Typography>
                      ) : (
                        <Typography as="p" className="text-gray-700 mb-4">
                          No summary notes available. Please read the full report.
                        </Typography>
                      )}

                      {report.content ? (
                        <Collapsible className="group">
                          <CollapsibleTrigger asChild>
                            <Button
                              variant="link"
                              className="p-0 h-auto text-blue-600 hover:text-blue-700 font-semibold"
                            >
                              <span className="group-data-[state=closed]:block group-data-[state=open]:hidden">
                                Read full summary
                              </span>
                              <span className="group-data-[state=closed]:hidden group-data-[state=open]:block">
                                Collapse summary
                              </span>
                            </Button>
                          </CollapsibleTrigger>
                          <CollapsibleContent className="mt-4">
                            <div className="prose max-w-none prose-slate">
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {report.content}
                              </ReactMarkdown>
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      ) : (
                        <Button
                          variant="link"
                          className="p-0 h-auto text-blue-600 hover:text-blue-700 font-semibold"
                        >
                          Read full summary
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )
            })()
          )}
        </RecommendationsSection>
      </div>

      <UploadDocumentModal
        open={uploadModalOpen}
        setOpen={setUploadModalOpen}
        estateId={estateId}
        documentType={willDocumentType}
        onSuccess={() => {
          queryUtils.documents.listByType.invalidate()
        }}
      />
    </Page>
  )
}
